import React, { useEffect, useRef, useCallback } from 'react';
import { CF_TURNSTILE_SITE_KEY } from '../../config';

/**
 * Cloudflare Turnstile Component
 *
 * This component renders the Cloudflare Turnstile widget for bot protection.
 * It loads the Turnstile script dynamically and handles token generation.
 *
 * @see https://developers.cloudflare.com/turnstile/
 */

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const Turnstile: React.FC<TurnstileProps> = ({
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const scriptLoadedRef = useRef<boolean>(false);

  const renderWidget = useCallback(() => {
    if (containerRef.current && window.turnstile && !widgetIdRef.current) {
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: CF_TURNSTILE_SITE_KEY,
          callback: onVerify,
          'error-callback': onError,
          'expired-callback': onExpire,
          theme,
          size,
        });
      } catch (error) {
        console.error('Error rendering Turnstile widget:', error);
      }
    }
  }, [onVerify, onError, onExpire, theme, size]);

  useEffect(() => {
    // Check if the script is already loaded
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]'
    );

    if (existingScript) {
      // Script exists, wait for it to load
      window.onTurnstileLoad = renderWidget;
      return;
    }

    if (!scriptLoadedRef.current) {
      scriptLoadedRef.current = true;

      // Set up callback for when script loads
      window.onTurnstileLoad = renderWidget;

      // Load Turnstile script
      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (error) {
          console.error('Error removing Turnstile widget:', error);
        }
      }
    };
  }, [renderWidget]);

  // Skip rendering if no site key is configured
  if (!CF_TURNSTILE_SITE_KEY) {
    console.warn('Turnstile site key not configured');
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '20px 0',
      }}
    />
  );
};

export default Turnstile;

